import React from 'react';
import { Alert, Card, Col, Row, Skeleton, Typography, theme } from 'antd';
import type { Dayjs } from 'dayjs';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { formatNumber } from '@leedpulse-monorepo/utils';
import { useLeedflowReliabilityStats } from '../../../../hooks/leedflow/useLeedflowReliabilityStats';
import { useContactsQueueStats } from '../../../../hooks/leedflow/useContactsQueueStats';
import type {
  LatencyStats,
  ReliabilityPushLegacyCallStats,
  ReliabilityPushLegStats,
} from '../../../../../domain/use_cases/leedflow/overview/getReliabilityStats';
import FaultyCallsPanel from '../FaultyCallsPanel';
import StatsTile from '../../../@shared/StatsTile';
import ErrorLogsPanel from '../ErrorLogsPanel';
import ContactsQueueCard from '../ContactsQueueCard';
import { WarningOutlined } from '@ant-design/icons';
import { formatNumber as formatCount } from '@leedpulse-monorepo/utils';
import type { CallsAggregate, FaultyCallEntry } from '../../../../../domain/use_cases/leedflow/overview/getOverviewStats';

const { useToken } = theme;

interface ReliabilityTabProps {
  period?: [Dayjs | null, Dayjs | null];
  subscriptionIds?: string[];
  exclude?: string[];
  /**
   * Compteur ET échantillon des appels en défaut, tous deux issus de la route
   * overview. La route fiabilité calcule son propre compteur, mais sans cache :
   * afficher l'un avec les lignes de l'autre laissait un total et une liste
   * mesurés à deux instants différents. Une seule source, donc.
   */
  faulty: CallsAggregate['faulty'];
  faultyRows: FaultyCallEntry[];
  faultyStatus: ContextStatus;
}

// Valeurs brutes vues côté backend (backend/src/helpers/livekit/voip-pipeline/voipcall-doc.ts,
// backend/src/helpers/livekit/voip-pipeline/sip-pipeline.ts). Une clé absente de ces
// tables (nouveau motif introduit côté backend) tombe sur le fallback de `labelFrom`
// et s'affiche telle quelle plutôt que de disparaître.
const VOICEMAIL_REASON_LABELS: Record<string, string> = {
  timeout_no_answer: 'Non décroché (délai dépassé)',
  divert_manual: 'Basculement manuel',
  vacation_mode: 'Mode vacances',
  do_not_disturb: 'Ne pas déranger',
  business_hours_closed: 'Hors horaires d\'ouverture',
  owner_busy: 'Déjà en communication',
  answering_stalled: 'Décroché signalé mais jamais rejoint',
  device_unreachable: 'Téléphone injoignable (push non envoyé)',
};

const RECORDING_PHASE_LABELS: Record<string, string> = {
  idle: 'Non démarré',
  recording: 'Enregistrement en cours',
  partial_relay: 'Renvoi partiel',
  fully_relayed: 'Renvoi complet',
  mixed: 'Pistes mixées',
  transcribing: 'Transcription en cours',
  transcribed: 'Transcrit',
  skipped: 'Ignoré (légitime)',
  failed: 'Échec',
};

const RESOLUTION_STATUS_LABELS: Record<string, string> = {
  ok: 'Contact rattaché',
  contact_not_found: 'Contact introuvable',
  owner_not_found: 'Propriétaire introuvable',
  voip_disabled: 'VoIP désactivé',
  owner_busy: 'Propriétaire déjà en ligne',
};

const labelFrom = (labels: Record<string, string>, key: string): string => labels[key] ?? key;

const buildBreakdownRows = (record: Record<string, number>, labels: Record<string, string>) => {
  const total = Object.values(record).reduce((sum, value) => sum + value, 0);
  return Object.entries(record)
    .sort((a, b) => b[1] - a[1])
    .map(([key, value]) => ({
      key,
      label: labelFrom(labels, key),
      value,
      pct: total > 0 ? (value / total) * 100 : 0,
    }));
};

const EmptyState: React.FC<{ message: string }> = ({ message }) => {
  const { token } = useToken();
  return (
    <div style={{ color: token.colorTextSecondary, textAlign: 'center', padding: '2rem 0' }}>
      {message}
    </div>
  );
};

const StatBlock: React.FC<{ label: string; value: number }> = ({ label, value }) => {
  const { token } = useToken();
  return (
    <div>
      <div style={{ fontSize: 12, color: token.colorTextSecondary }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {formatNumber(value)}
      </div>
    </div>
  );
};

const CountRows: React.FC<{ rows: ReturnType<typeof buildBreakdownRows> }> = ({ rows }) => {
  const { token } = useToken();
  if (rows.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map((row) => (
        <div
          key={row.key}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            borderRadius: token.borderRadiusSM,
            padding: '6px 10px',
            background: token.colorFillTertiary,
          }}
        >
          <span>{row.label}</span>
          <span style={{ color: token.colorTextSecondary, fontVariantNumeric: 'tabular-nums' }}>
            {formatNumber(row.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

// Barres de répartition (part en % parmi les clés connues de ce record), même
// rendu que `BreakdownPanel.tsx` — gardé cohérent avec le reste du chantier.
const BreakdownBars: React.FC<{ rows: ReturnType<typeof buildBreakdownRows> }> = ({ rows }) => {
  const { token } = useToken();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {rows.map((row) => (
        <div
          key={row.key}
          style={{
            position: 'relative',
            borderRadius: token.borderRadiusSM,
            overflow: 'hidden',
            padding: '6px 10px',
            background: token.colorFillTertiary,
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              width: `${row.pct}%`,
              background: token.colorPrimaryBg,
            }}
          />
          <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <span>{row.label}</span>
            <span style={{ color: token.colorTextSecondary, fontVariantNumeric: 'tabular-nums' }}>
              {formatNumber(row.value)} · {row.pct.toFixed(1)}%
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

interface BreakdownCardProps {
  title: string;
  caption: string;
  record: Record<string, number>;
  labels: Record<string, string>;
  status: ContextStatus;
}

// Rend les trois blocs "répartition d'une clé libre backend" (motifs
// messagerie, phases de transcription, rattachement contact) avec le même
// habillage : une clé absente du record = aucune occurrence sur la période,
// même convention que `BreakdownPanel`/`TopSubscriptionsPanel` (pas de
// sentinelle `null` disponible côté contrat pour ces `Record<string, number>`,
// contrairement aux `LatencyStats`).
const BreakdownCard: React.FC<BreakdownCardProps> = ({ title, caption, record, labels, status }) => {
  const rows = buildBreakdownRows(record, labels);

  return (
    <Card title={title}>
      {status === ContextStatus.LOADING ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : rows.length === 0 ? (
        <EmptyState message="Aucune donnée sur la période" />
      ) : (
        <>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            {caption}
          </Typography.Text>
          <BreakdownBars rows={rows} />
        </>
      )}
    </Card>
  );
};

interface LatencyCardProps {
  title: string;
  caption: string;
  stats: LatencyStats;
  status: ContextStatus;
}

// `p50`/`p90` à `null` veut dire « aucun appel VoIP sur la période », pas
// « latence nulle » : les deux ne sont jamais affichés comme un `0`.
const LatencyCard: React.FC<LatencyCardProps> = ({ title, caption, stats, status }) => {
  const hasData = stats.p50 !== null && stats.p90 !== null;

  return (
    <Card title={title}>
      {status === ContextStatus.LOADING ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : !hasData ? (
        <EmptyState message="Aucun appel VoIP sur la période" />
      ) : (
        <>
          <div style={{ display: 'flex', gap: 24, marginBottom: 8 }}>
            <StatBlock label="p50 (médiane)" value={stats.p50 as number} />
            <StatBlock label="p90" value={stats.p90 as number} />
          </div>
          <Typography.Text type="secondary">{caption} Valeurs en millisecondes.</Typography.Text>
        </>
      )}
    </Card>
  );
};

const PushLegCard: React.FC<{ stats: ReliabilityPushLegStats; status: ContextStatus }> = ({ stats, status }) => {
  const totalLegs = stats.sent + stats.failed + stats.excluded;
  const errorRows = buildBreakdownRows(stats.byError, {});

  return (
    <Card title="Push VoIP par leg (device)">
      {status === ContextStatus.LOADING ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : totalLegs === 0 ? (
        <EmptyState message="Aucune donnée sur la période" />
      ) : (
        <>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Unité : un leg, c'est-à-dire un couple token/device. Un appel a plusieurs legs.
            Ne jamais additionner avec le bloc « par appel (historique) » ci-contre.
          </Typography.Text>
          <div style={{ display: 'flex', gap: 24, marginBottom: 12 }}>
            <StatBlock label="Envoyés" value={stats.sent} />
            <StatBlock label="Échec" value={stats.failed} />
            <StatBlock label="Exclus" value={stats.excluded} />
          </div>
          <CountRows rows={errorRows} />
        </>
      )}
    </Card>
  );
};

const PushLegacyCard: React.FC<{ stats: ReliabilityPushLegacyCallStats; status: ContextStatus }> = ({ stats, status }) => {
  const errorRows = buildBreakdownRows(stats.byError, {});

  return (
    <Card title="Push VoIP par appel (historique)">
      {status === ContextStatus.LOADING ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : stats.total === 0 ? (
        <EmptyState message="Aucun appel non instrumenté par leg sur la période" />
      ) : (
        <>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Unité : l'appel entier, antérieur à l'instrumentation par leg, sans détail
            par device. Ne jamais additionner avec le bloc « par leg » ci-contre : mélanger
            les deux unités gonflerait un taux d'échec qu'aucun succès ne viendrait compenser.
          </Typography.Text>
          <div style={{ display: 'flex', gap: 24, marginBottom: 12 }}>
            <StatBlock label="Appels non instrumentés" value={stats.total} />
            <StatBlock label="Dont en échec" value={stats.failed} />
          </div>
          <CountRows rows={errorRows} />
        </>
      )}
    </Card>
  );
};

const ReliabilityTab: React.FC<ReliabilityTabProps> = ({
  period,
  subscriptionIds,
  exclude,
  faulty,
  faultyRows,
  faultyStatus,
}) => {
  const { stats, status } = useLeedflowReliabilityStats(period, subscriptionIds, exclude);
  const { stats: contactsQueueStats, status: contactsQueueStatus } = useContactsQueueStats();
  const { token } = useToken();

  if (status === ContextStatus.ERROR) {
    return (
      <Alert
        type="error"
        showIcon
        message="Impossible de charger les données"
        description="Les statistiques de fiabilité n'ont pas pu être récupérées. Réessayez ou changez la période."
      />
    );
  }

  return (
    <Row gutter={[16, 16]}>
      {/* En tête d'onglet : les appels en défaut ne disent rien du volume
          d'activité, ils disent si la chaîne d'appel tient. Leur place est ici,
          pas au milieu des statistiques d'usage. */}
      <Col xs={24} md={8}>
        <div style={{ height: 184, display: 'flex' }}>
          <StatsTile
            isLoading={faultyStatus === ContextStatus.LOADING}
            isConfidential={false}
            item={{
              icon: <WarningOutlined />,
              title: 'Appels en défaut',
              value: formatCount(faulty.total),
              color: token.colorWarning,
            }}
          />
        </div>
      </Col>
      <Col xs={24} md={16}>
        <FaultyCallsPanel faulty={faulty} rows={faultyRows} status={faultyStatus} />
      </Col>

      <Col xs={24} md={12}>
        <PushLegCard stats={stats.push.legs} status={status} />
      </Col>
      <Col xs={24} md={12}>
        <PushLegacyCard stats={stats.push.legacyCalls} status={status} />
      </Col>

      <Col xs={24} md={12}>
        <LatencyCard
          title="Latence d'envoi du push (dispatch)"
          caption="Temps entre le démarrage de l'appel et l'envoi du push par notre backend, pas la réception par le téléphone (mesure réservée à pushDeliveryLatencyMs, bloquée par LEED-1723)."
          stats={stats.pushDispatchLatencyMs}
          status={status}
        />
      </Col>
      <Col xs={24} md={12}>
        <LatencyCard
          title="Latence de décroché"
          caption="Temps entre le démarrage de l'appel et le décroché côté app."
          stats={stats.answerLatencyMs}
          status={status}
        />
      </Col>

      <Col xs={24} md={12}>
        <BreakdownCard
          title="Motifs de basculement en messagerie"
          caption="Part parmi les appels ayant basculé en messagerie sur la période."
          record={stats.voicemailReasons}
          labels={VOICEMAIL_REASON_LABELS}
          status={status}
        />
      </Col>
      <Col xs={24} md={12}>
        <BreakdownCard
          title="Phases de transcription"
          caption="Dernière phase atteinte par le pipeline d'enregistrement / transcription."
          record={stats.recordingPhases}
          labels={RECORDING_PHASE_LABELS}
          status={status}
        />
      </Col>

      <Col xs={24} md={12}>
        <BreakdownCard
          title="Rattachement au contact"
          caption="Résolution du contact / propriétaire à la prise d'appel."
          record={stats.resolutionStatus}
          labels={RESOLUTION_STATUS_LABELS}
          status={status}
        />
      </Col>
      {/* Santé de la queue d'import, tous comptes confondus : ni la période ni
          les filtres ne s'y appliquent. */}
      <Col xs={24} md={12}>
        <ContactsQueueCard stats={contactsQueueStats} status={contactsQueueStatus} />
      </Col>
      <Col xs={24}>
        <ErrorLogsPanel />
      </Col>
    </Row>
  );
};

export default ReliabilityTab;
